import { createTheme } from '@mui/material/styles';

// Основная тема приложения Sphere Platform в стиле glassmorphism
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#5B8DEF',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF8E53',
    },
    background: {
      default: '#0b0f1a',
      paper: 'rgba(255, 255, 255, 0.08)',
    },
    text: {
      primary: 'rgba(255,255,255,0.92)',
      secondary: 'rgba(255,255,255,0.72)'
    }
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(180deg, #0b0f1a 0%, #0f172a 60%, #0b0f1a 100%)',
          color: 'rgba(255,255,255,0.92)'
        },
        '*::-webkit-scrollbar': { height: 8, width: 8 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255,255,255,0.2)',
          borderRadius: 8,
        },
      },
    },
    MuiContainer: {
      defaultProps: { maxWidth: 'lg' },
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
          '@media (min-width:900px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)'
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)'
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          boxShadow: 'none'
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'transparent',
          border: 'none',
          boxShadow: 'none',
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.12)',
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.12)',
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.12)',
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.18)'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.28)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main
          },
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(8px)',
        })
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,0.7)'
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'rgba(255,255,255,0.9)'
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)'
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        },
        head: {
          color: 'rgba(255,255,255,0.9)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
        },
        contained: {
          backgroundColor: 'rgba(255,255,255,0.16)',
          color: '#fff',
          backdropFilter: 'blur(8px)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.22)'
          }
        },
        containedPrimary: {
          color: '#fff',
          background: 'linear-gradient(135deg, #5B8DEF 0%, #3E64E8 50%, #2E49CF 100%)',
          boxShadow: '0 8px 24px rgba(59,105,239,0.35)',
          border: '1px solid rgba(255,255,255,0.10)',
          '&:hover': {
            background: 'linear-gradient(135deg, #6C99F1 0%, #4A73F0 45%, #3556D8 100%)',
            boxShadow: '0 10px 28px rgba(59,105,239,0.45)'
          },
          '&:disabled': {
            background: 'linear-gradient(135deg, rgba(91,141,239,0.45) 0%, rgba(62,100,232,0.45) 100%)',
            boxShadow: 'none'
          }
        },
        containedSuccess: {
          color: '#fff',
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 45%, #10b981 100%)',
          boxShadow: '0 8px 24px rgba(16,185,129,0.35)',
          border: '1px solid rgba(255,255,255,0.10)',
          '&:hover': {
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 30%, #059669 100%)',
            boxShadow: '0 10px 28px rgba(5,150,105,0.45)'
          },
          '&:disabled': {
            background: 'linear-gradient(135deg, rgba(34,197,94,0.4) 0%, rgba(22,163,74,0.4) 100%)',
            boxShadow: 'none'
          }
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.28)',
          color: '#fff',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.4)'
          }
        },
        outlinedPrimary: {
          color: '#D7E5FF',
          borderColor: 'rgba(91,141,239,0.6)',
          '&:hover': {
            borderColor: 'rgba(91,141,239,0.85)',
            backgroundColor: 'rgba(91,141,239,0.12)'
          }
        },
        textPrimary: {
          color: '#D7E5FF',
          '&:hover': { backgroundColor: 'rgba(91,141,239,0.12)' }
        }
      },
    },
  },
});

export default theme;